import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Stack,
  Switch,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import CurrencyInput from "react-currency-input-field";
import { FaTrashAlt } from "react-icons/fa";
import { FiPlus } from "react-icons/fi";
import InputWithLabel from "../InputWithLabel";
import { TicketTypeTier, TicketTypeValues } from "../schema";
import { useCallback, useState } from "react";
import DatepickerFormInput from "../../DatepickerFormInput";

const TIER_LIMIT = 9;

const TieredAllocation = ({
  currency,
  loading = false,
  timezone,
  onRemoveTier,
}: {
  currency: string;
  loading: boolean;
  timezone: string;
  onRemoveTier: (tier: TicketTypeTier) => Promise<void>;
}) => {
  const {
    register,
    control,
    setValue,
    watch,
    trigger,
    formState: { errors, defaultValues },
  } = useFormContext<TicketTypeValues>();
  const { append, remove } = useFieldArray({
    control,
    keyName: "field_id",
    name: "tiers",
  });

  const currentTiers = watch("tiers");
  const [isTierCodesOpen, setIsTierCodesOpen] = useState(
    currentTiers.reduce((tiersIsOpen, tier) => {
      return {
        ...tiersIsOpen,
        [tier.id]: tier.code ? true : tier.hidden,
      };
    }, {})
  );

  const tiers = currentTiers.filter((t) => t.status !== "DELETED");

  const updateTierCodeOpen = useCallback(
    (tierId: string, isOpen: boolean) => {
      setIsTierCodesOpen((isTierCodesOpen) => {
        return {
          ...isTierCodesOpen,
          [tierId]: isOpen,
        };
      });
    },
    [setIsTierCodesOpen]
  );

  const isEditIndex = (index) => watch(`tiers.${index}.id`);
  const isEdit = !!watch("id");

  const hasError = (index, prop) => {
    if (!errors["tiers"]) return false;

    if (!errors["tiers"][index]) return false;

    return prop in errors["tiers"][index];
  };

  const getErrorFor = (index: number, prop: string) => {
    if (!errors["tiers"]) return undefined;

    if (index in errors["tiers"]) {
      if (errors["tiers"][index]) {
        if (prop in errors["tiers"][index]) {
          return errors["tiers"][index][prop].message;
        }
        return;
      }
      return;
    }
    return;
  };

  const movesTicketsToNextBucket = watch("shift_quantity_on_expiration");

  const tierCanBeDeleted = (tier, index) => {
    return (
      !isEdit ||
      tier.isNew ||
      (tier.display_options?.delete_options?.includes("DELETE") && index !== 0)
    );
  };

  return (
    <>
      {tiers.length ? (
        <HStack w="100%" mb={1} gap={0}>
          <Text w={isEdit ? "25%" : "21%"}>Name</Text>
          <Text w="17%">Quantity</Text>
          <Text w="24%">Price</Text>
          <Text>
            Expires at {movesTicketsToNextBucket ? "(*)" : "(Optional)"}
          </Text>
        </HStack>
      ) : null}
      <VStack w="full" spacing={4}>
        {tiers.map((t, index) => (
          <VStack
            alignItems={"flex-start"}
            key={t.field_id}
            borderBottomWidth={1}
            borderBottomColor={"#ddd"}
            paddingBottom={4}
          >
            <HStack w="100%" key={t.id} alignItems={"flex-start"}>
              <VStack spacing={1} w="25%">
                <Input
                  required
                  placeholder="Name"
                  isInvalid={hasError(index, "name")}
                  {...register(`tiers.${index}.name`, {
                    onChange: () => trigger(`tiers.${index}.name`),
                    disabled: loading,
                  })}
                />
                <Box h={4} w="full">
                  {hasError(index, "name") ? (
                    <Text
                      fontSize={"xs"}
                      color="red.300"
                      w="full"
                      lineHeight={4}
                    >
                      {`${getErrorFor(index, "name")}`}
                    </Text>
                  ) : null}
                </Box>
              </VStack>
              <VStack spacing={1} w="18%">
                <Input
                  required
                  inputMode="numeric"
                  type="number"
                  isDisabled={t.sold_out}
                  placeholder="Qty"
                  disabled={loading || t.sold_out}
                  isInvalid={hasError(index, "quantity")}
                  min={
                    isEditIndex(index)
                      ? defaultValues?.tiers[index]?.quantity
                      : 1
                  }
                  {...register(`tiers.${index}.quantity`, {
                    min: isEditIndex(index)
                      ? defaultValues?.tiers[index]?.quantity
                      : 1,
                  })}
                />
                <Box h={4} w="full">
                  {hasError(index, "quantity") ? (
                    <Text fontSize={"xs"} color="red.300" lineHeight={4}>
                      {`${getErrorFor(index, "quantity")}`}
                    </Text>
                  ) : null}
                </Box>
              </VStack>

              <Controller
                control={control}
                name={`tiers.${index}.price`}
                render={({ field }) => (
                  <VStack spacing={1} w="25%">
                    <CurrencyInput
                      //@ts-ignore
                      style={{ width: "100%" }}
                      //@ts-ignore
                      borderColor={hasError(index, "price") ? "red" : "inherit"}
                      //@ts-ignore
                      borderWidth={hasError(index, "price") ? 2 : 1}
                      placeholder="Price"
                      required
                      value={field.value}
                      disabled={loading || t.sold_out}
                      onValueChange={(value) => {
                        setValue(`tiers.${index}.price`, value || "", {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        trigger(`tiers.${index}.price`);
                      }}
                      intlConfig={{
                        locale: "en-US",
                        currency,
                      }}
                      customInput={Input}
                      step={1}
                    />
                    <Box h={4} w="full">
                      {hasError(index, "price") ? (
                        <Text fontSize={"xs"} color="red.300" lineHeight={4}>
                          {`${getErrorFor(index, "price")}`}
                        </Text>
                      ) : null}
                    </Box>
                  </VStack>
                )}
              />
              <VStack minW={"34%"} w="34%">
                <DatepickerFormInput
                  skipValidate
                  method="onChange"
                  overrideError={getErrorFor(index, "expires_at")}
                  deps={[`tiers.${index + 1}`, `tiers.${index - 1}`]}
                  name={`tiers.${index}.expires_at`}
                  timezone={timezone}
                  dateTimeProps={{
                    disabled: loading || t.sold_out,
                    disablePast: true,
                    slotProps: {
                      actionBar: {
                        actions: ["clear", "accept"],
                      },
                    },
                    sx: {
                      "& .MuiInputBase-input": {
                        padding: "8px",
                      },
                      ...(hasError(index, "expires_at") && {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "red",
                          borderWidth: "2px",
                        },
                      }),
                    },
                  }}
                />
              </VStack>
              {tierCanBeDeleted(t, index) ? (
                <IconButton
                  w="9%"
                  isDisabled={loading}
                  aria-label={`Delete tier ${index}`}
                  variant={"ghost"}
                  onClick={() => {
                    if (t.id.startsWith("_CREATE_")) {
                      remove(index);
                    } else {
                      onRemoveTier(t);
                    }
                  }}
                  icon={<FaTrashAlt fontSize={"1.0rem"} />}
                />
              ) : null}
            </HStack>
            <VStack
              w="full"
              justifyContent={"space-between"}
              alignItems={"flex-start"}
            >
              <Accordion w="full" allowMultiple>
                <AccordionItem w="full">
                  <h2>
                    <AccordionButton>
                      <Box
                        fontWeight={"medium"}
                        as="span"
                        flex="1"
                        textAlign="left"
                      >
                        Settings
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    <Stack w="full">
                      <HStack>
                        <Stack minW={"250px"} spacing={0}>
                          <Text>On Sale Date</Text>
                          <DatepickerFormInput
                            method="onChange"
                            overrideError={getErrorFor(index, "on_sale_at")}
                            deps={[
                              `tiers.${index + 1}`,
                              `tiers.${index - 1}`,
                              `tiers.${index}.off_sale_at`,
                            ]}
                            name={`tiers.${index}.on_sale_at`}
                            timezone={timezone}
                            dateTimeProps={{
                              disabled: loading,
                              disablePast: true,
                              slotProps: {
                                actionBar: {
                                  actions: ["clear", "accept"],
                                },
                              },
                              sx: {
                                "& .MuiInputBase-input": {
                                  padding: "8px",
                                },
                              },
                            }}
                          />
                        </Stack>
                        <Stack minW={"250px"} spacing={0}>
                          <Text>Off Sale Date</Text>
                          <DatepickerFormInput
                            method="onChange"
                            overrideError={getErrorFor(index, "off_sale_at")}
                            deps={[
                              `tiers.${index + 1}`,
                              `tiers.${index - 1}`,
                              `tiers.${index}.on_sale_at`,
                            ]}
                            name={`tiers.${index}.off_sale_at`}
                            timezone={timezone}
                            dateTimeProps={{
                              disabled: loading,
                              disablePast: true,
                              slotProps: {
                                actionBar: {
                                  actions: ["clear", "accept"],
                                },
                              },
                              sx: {
                                "& .MuiInputBase-input": {
                                  padding: "8px",
                                },
                              },
                            }}
                          />
                        </Stack>
                      </HStack>
                      <Stack
                        spacing={0}
                        justifyContent={"space-between"}
                        w="full"
                      >
                        <Text>Hidden</Text>
                        <HStack gap={4}>
                          <Switch
                            colorScheme="green"
                            defaultChecked={!!t.code || t.hidden}
                            isDisabled={loading || !!t.code}
                            onChange={() => {
                              const changingTo = !isTierCodesOpen[t.id];

                              setValue(`tiers.${index}.hidden`, changingTo, {
                                shouldDirty: true,
                              });
                              updateTierCodeOpen(t.id, changingTo);
                            }}
                            ml={2}
                          />
                          <Text fontWeight={"medium"}>
                            Require code to unlock
                          </Text>
                        </HStack>
                      </Stack>
                    </Stack>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </VStack>
          </VStack>
        ))}
        {tiers.length <= TIER_LIMIT ? (
          <Button
            w="full"
            leftIcon={<FiPlus />}
            isDisabled={loading}
            onClick={() => {
              append({
                isNew: true,
                id: `_CREATE_${tiers.length}_`,
                name: "",
                price: "",
                quantity: "",
                code: null,
                expires_at: null,
              });
              trigger("tiers");
            }}
          >
            Add Tier
          </Button>
        ) : null}
        <HStack w="100%">
          <HStack w="40%">
            <InputWithLabel
              name="limit"
              required
              inputProps={{
                disabled: loading,
              }}
              label="Limit"
              placeholder="Limit"
            >
              <Text>(Max 100)</Text>
            </InputWithLabel>
          </HStack>
        </HStack>
      </VStack>
    </>
  );
};

export default TieredAllocation;
